import React, { useState } from 'react';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Alert,
  AlertIcon,
  Text,
  Checkbox,
  useToast,
  FormErrorMessage
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useTranslation } from 'react-i18next';

const RegistroCliente = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellidos: '',
    telefono: '',
    habilitado: false,
    acepto_politicas: false,
    forma_pago: 'Bizum',
    registration_source: 'registration_page',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [passwordError, setPasswordError] = useState('');

  const validatePassword = (password) => {
    let currentPasswordError = '';
    if (password.length < 8) {
      currentPasswordError = t('registro_cliente_password_min');
    } else if (!/[a-z]/.test(password)) {
      currentPasswordError = t('registro_cliente_password_lower');
    } else if (!/[A-Z]/.test(password)) {
      currentPasswordError = t('registro_cliente_password_upper');
    } else if (!/[0-9]/.test(password)) {
      currentPasswordError = t('registro_cliente_password_number');
    }
    setPasswordError(currentPasswordError);
    return currentPasswordError === '';
  };

  const getDeviceInfo = () => {
    // Generar o recuperar custom_device_id
    let customDeviceId = localStorage.getItem('custom_device_id');
    if (!customDeviceId) {
      customDeviceId = `device_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      localStorage.setItem('custom_device_id', customDeviceId);
    }

    const deviceInfo = {
      user_agent: navigator.userAgent || null,
      accept_language: navigator.language || null,
      app_version: navigator.appVersion || null,
      platform: navigator.platform || null,
      vendor: navigator.vendor || null,
      origin: window.location.origin,
      referer: document.referrer || null,
      connection_type: navigator.connection ? navigator.connection.effectiveType : null,
      custom_device_id: customDeviceId,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      screen_color_depth: window.screen.colorDepth,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      languages: navigator.languages ? navigator.languages.join(', ') : navigator.language,
      online: navigator.onLine,
      cookie_enabled: navigator.cookieEnabled,
      do_not_track: navigator.doNotTrack,
      hardware_concurrency: navigator.hardwareConcurrency || null,
      device_memory: navigator.deviceMemory || null,
      max_touch_points: navigator.maxTouchPoints || 0,
      latitude: null,
      longitude: null,
      location_accuracy: null,
      address: null
    };

    // Intentar obtener ubicación con timeout
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        const timeoutId = setTimeout(() => {
          resolve(deviceInfo); // Resolver sin geolocalización después de 5s
        }, 5000);

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            clearTimeout(timeoutId);
            deviceInfo.latitude = position.coords.latitude;
            deviceInfo.longitude = position.coords.longitude;
            deviceInfo.location_accuracy = position.coords.accuracy;
            
            // Obtener dirección mediante geocodificación inversa
            try {
              const latlng = `${position.coords.latitude},${position.coords.longitude}`;
              const response = await fetch(`/api/maps/geocode/?latlng=${latlng}`);
              if (response.ok) {
                const data = await response.json();
                deviceInfo.address = data.address || null;
              }
            } catch (error) {
              console.error('Error al obtener dirección:', error);
            }
            resolve(deviceInfo);
          },
          (error) => {
            clearTimeout(timeoutId);
            console.error('Error al obtener geolocalización:', error);
            resolve(deviceInfo); // Resolver sin geolocalización si hay error
          }
        );
      } else {
        resolve(deviceInfo); // Resolver sin geolocalización si no está disponible
      }
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevFormData) => {
      const newFormData = {
        ...prevFormData,
        [name]: type === 'checkbox' ? checked : value,
      };
      if (name === 'password') {
        validatePassword(newFormData.password);
      }
      return newFormData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const isFormComplete = formData.email && formData.password && formData.nombre && formData.telefono && formData.acepto_politicas;
    const isPasswordValid = validatePassword(formData.password);

    if (!isFormComplete || !isPasswordValid) {
      setError(t('registro_cliente_error_campos'));
      toast({
        title: t('registro_cliente_error_validacion'),
        description: t('registro_cliente_error_campos'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const url = `${baseURL}/api/clientes/`;

    // Recoger datos del dispositivo
    const device_info = await getDeviceInfo();

    // Agregar datos del dispositivo al formData
    const dataToSend = {
      ...formData,
      device_info
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`${t('registro_cliente_error_registro')}: ${response.status} - ${errorData?.detail || response.statusText}`);
      }

      const data = await response.json();
      toast({
        title: t('registro_cliente_exito'),
        description: t('registro_cliente_exito_desc'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setFormData({
        email: '',
        password: '',
        nombre: '',
        apellidos: '',
        telefono: '',
        habilitado: false,
        acepto_politicas: false,
        forma_pago: 'Bizum',
        registration_source: 'registration_page',
      });
      setPasswordError('');
      setTimeout(() => navigate(`/verificar-codigo?type=cliente&email=${data.email}`), 2000);
    } catch (err) {
      setError(err.message);
      toast({
        title: t('registro_cliente_error_registro'),
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={4} borderWidth="1px" borderRadius="lg" maxWidth="500px" mx="auto" mt={8}>
      <Heading as="h2" size="xl" mb={6} textAlign="center">{t('registro_cliente_titulo')}</Heading>
      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <FormControl isRequired mb={4}>
          <FormLabel htmlFor="email">{t('registro_cliente_email')}</FormLabel>
          <Input type="email" id="email" name="email" value={formData.email} onChange={handleChange} />
        </FormControl>

        <FormControl isRequired mb={4} isInvalid={!!passwordError}>
          <FormLabel htmlFor="password">{t('registro_cliente_password')}</FormLabel>
          <Input type="password" id="password" name="password" value={formData.password} onChange={handleChange} />
          {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
        </FormControl>

        <FormControl isRequired mb={4}>
          <FormLabel htmlFor="nombre">{t('registro_cliente_nombre')}</FormLabel>
          <Input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} />
        </FormControl>

        <FormControl isRequired mb={4}>
          <FormLabel htmlFor="telefono">{t('registro_cliente_telefono')}</FormLabel>
          <PhoneInput
            country={'es'}
            value={formData.telefono}
            onChange={phone => setFormData(prev => ({ ...prev, telefono: '+' + phone }))}
            inputProps={{
              name: 'telefono',
              required: true,
              autoFocus: false
            }}
            inputStyle={{
              width: '100%',
              background: '#000',
              color: '#fff',
            }}
            specialLabel=""
          />
        </FormControl>

        <FormControl isRequired display="flex" alignItems="center" mb={6}>
          <FormLabel htmlFor="acepto_politicas" mb="0" mr={2}>
            {t('registro_cliente_acepto_politicas')}
          </FormLabel>
          <Checkbox
            id="acepto_politicas"
            name="acepto_politicas"
            isChecked={formData.acepto_politicas}
            onChange={handleChange}
          />
        </FormControl>

        <Button
          type="submit"
          colorScheme="blue"
          isLoading={loading}
          isDisabled={!formData.email || !formData.password || !formData.nombre || !formData.telefono || !formData.acepto_politicas || !!passwordError || loading}
          width="full"
        >
          {t('registro_cliente_registrarme')}
        </Button>
      </form>
      <Text mt={4} textAlign="center">
        {t('registro_cliente_ya_tienes_cuenta')}{' '}
        <Button variant="link" colorScheme="blue" onClick={() => navigate('/login')}>
          {t('registro_cliente_inicia_sesion')}
        </Button>
      </Text>
    </Box>
  );
};

export default RegistroCliente;